/**
 * Shared rules for the images a user can attach. A file picker's `accept` list only filters the
 * picker's own dialog — most platforms still let the user override it, and drag and drop bypasses it
 * entirely — so anything that takes a file validates it here rather than trusting `accept`.
 */

/** The types blob storage accepts, and the `accept` list the upload panels advertise. */
export const acceptedImageTypes = ["image/jpeg", "image/png", "image/webp"] as const;

/** Matches the "Max 10MB" the upload panels promise. */
export const maxImageSizeBytes = 10 * 1024 * 1024;

/** Ready-made `accept` attribute value, so the picker and the validator cannot drift apart. */
export const acceptedImageTypesAttribute = acceptedImageTypes.join(",");

/** Why a file was rejected. Callers map this to a translated message. */
export type ImageFileRejection = "type" | "size";

/** Returns the reason the file is unusable, or null when it passes. */
export const validateImageFile = (file: File): ImageFileRejection | null => {
	if (!(acceptedImageTypes as readonly string[]).includes(file.type)) {
		return "type";
	}

	if (file.size > maxImageSizeBytes) {
		return "size";
	}

	return null;
};
